/**
 * Role → group resolution, sourced from the enums API.
 *
 * Replaces the old constants.js `USER_GROUP_MAP` + `computeGroups()`.
 * The role→group data lives in the `user.role_groups` enum namespace
 * (each value = a role, flags.group = its group). The admin→staff
 * inheritance rule stays here in code — it's logic, not data.
 */

import { getEnumOptions } from '../api/enumApi';

const ROLE_GROUPS_NS = 'user.role_groups';

/**
 * Fetch the role→group map from the enum: { role: group }.
 * @returns {Promise<Record<string,string>>}
 */
export async function fetchRoleGroupMap() {
  const options = await getEnumOptions(ROLE_GROUPS_NS);
  const map = {};
  for (const opt of options) {
    const group = opt.flags?.group;
    if (group) map[opt.value] = group;
  }
  return map;
}

/**
 * Derive a deduplicated list of groups from a role array.
 * superadmin/admin → admin group AND inherits staff (mirrors legacy computeGroups).
 *
 * @param {string[]} roleList
 * @returns {Promise<string[]>}
 */
export async function computeGroups(roleList = []) {
  const map = await fetchRoleGroupMap();
  const groupSet = new Set();
  for (const role of roleList) {
    const g = map[role];
    if (g) groupSet.add(g);
  }
  // Inheritance: admin always gets staff access too
  if (groupSet.has('admin')) groupSet.add('staff');
  return Array.from(groupSet);
}
